import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Select, Table, SearchField } from '@dreamtree-org/twreact-ui';
import { Plus, Upload, Search } from 'lucide-react';
import { PageHeader, StatusBadge, QueryState, EmptyState } from '@/components';
import { formatINR, formatDate } from '@/lib/format';
import Can from '@rbac/Can';
import { useQuery } from '@/lib/useQuery';
import { useCrudList, useCrudCount } from '../hooks';
import ReadinessBadge from '../components/ReadinessBadge';
import StatusTabs from '../components/StatusTabs';
import UnitDrawer from '../components/UnitDrawer';
import { labelOf, modelName, modelSpecs, unitMatches } from '../lib/catalog';
import { useProductTypes } from '@/lib/productTypes';
import { conditionOptions, gradeOptions } from '@/lib/conditions';

const LIMIT = 200;
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'in_stock', label: 'In stock' },
  { key: 'reserved', label: 'Reserved' },
  { key: 'sold', label: 'Sold' },
];


/** SELL-02 — real inventory list with status tabs, filters, search and a detail drawer. `embedded`: shown as the Products "In stock" tab. */
export default function Inventory({ embedded = false }) {
  const nav = useNavigate();
  const registry = useProductTypes();
  const [tab, setTab] = useState('all');
  const [grade, setGrade] = useState('');
  const [condition, setCondition] = useState('');
  const [brand, setBrand] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(null);

  const where = {};
  if (tab !== 'all') where.stock_state = tab;
  if (grade) where.grade = grade;
  if (condition) where.condition = condition;
  if (brand) where['ProductModel.brand_id'] = Number(brand);
  const units = useCrudList('InventoryUnit', { where, with: ['ProductModel'], orderBy: { column: 'id', direction: 'desc' }, limit: LIMIT });
  const brandList = useCrudList('Brand', { orderBy: { column: 'name', direction: 'asc' }, limit: 1000 });

  const counts = {
    all: useCrudCount('InventoryUnit', {}),
    in_stock: useCrudCount('InventoryUnit', { stock_state: 'in_stock' }),
    reserved: useCrudCount('InventoryUnit', { stock_state: 'reserved' }),
    sold: useCrudCount('InventoryUnit', { stock_state: 'sold' }),
  };
  const rows = useMemo(() => (units.data || []).filter((u) => unitMatches(u, term)), [units.data, term]);
  // MERCH-58: stock location and publish readiness for the loaded units, in one call.
  const ids = useMemo(() => (units.data || []).map((u) => u.id), [units.data]);
  const overview = useQuery('unitOverview', { ids }, { select: (r) => (r && r.data) || {}, enabled: ids.length > 0 });
  const info = overview.data || {};
  const filtered = !!(grade || condition || brand || term);
  const reloadAll = () => {
    units.reload();
    overview.reload();
    Object.values(counts).forEach((c) => c.reload());
  };

  const columns = [
    {
      key: 'model', label: 'Model',
      render: (u) => (
        <div className="min-w-0">
          <div className="font-medium">{modelName(u.ProductModel)}</div>
          <div className="text-xs opacity-70">{modelSpecs(u.ProductModel)}</div>
        </div>
      ),
    },
    { key: 'internal_sku', label: 'SKU' },
    {
      // A stock lot (D60) shows what is free to sell and what open orders hold, instead of a serial.
      key: 'serial_masked', label: 'Serial / stock',
      render: (u) => (u.stock_mode === 'quantity'
        ? <span className="text-sm">{Math.max(0, u.qty_on_hand - u.qty_reserved)} free{u.qty_reserved ? ` · ${u.qty_reserved} held` : ''}</span>
        : <span className="font-mono text-sm">{u.serial_masked || '—'}</span>),
    },
    { key: 'grade', label: 'Grade', render: (u) => `Grade ${u.grade}` },
    { key: 'condition', label: 'Condition', render: (u) => labelOf(u.condition), hideOnMobile: true },
    { key: 'battery_health_pct', label: 'Battery', render: (u) => (u.battery_health_pct != null ? `${u.battery_health_pct}%` : '—'), hideOnMobile: true },
    { key: 'acquisition_cost_paise', label: 'Cost', render: (u) => formatINR(u.acquisition_cost_paise), hideOnMobile: true },
    {
      key: 'location', label: 'Location', hideOnMobile: true,
      render: (u) => {
        const loc = info[u.id] && info[u.id].location;
        if (!loc) return <span className="opacity-60">—</span>;
        return <span title={loc.active ? undefined : 'This location is no longer active'}>{loc.name}{loc.active ? '' : ' (inactive)'}</span>;
      },
    },
    { key: 'stock_state', label: 'Status', render: (u) => <StatusBadge status={u.stock_state} /> },
    { key: 'readiness', label: 'Readiness', render: (u) => (u.stock_state === 'in_stock' ? <ReadinessBadge result={info[u.id]} /> : <span className="opacity-60">—</span>) },
    { key: 'created_at', label: 'Received', render: (u) => formatDate(u.created_at), hideOnMobile: true },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={embedded ? undefined : 'Inventory'}
        subtitle="Every device you hold, and stock sold by quantity. Serials are stored hashed; only the masked form is shown."
        actions={
          <Can module="inventory" action="create">
            <Button variant="outline" leftIcon={<Upload size={16} />} onClick={() => nav('/inventory/import')}>Import CSV</Button>
            {embedded ? null : <Button leftIcon={<Plus size={16} />} onClick={() => nav('/add')}>Add listing</Button>}
          </Can>
        }
      />

      <StatusTabs value={tab} onChange={setTab} tabs={TABS.map((t) => ({ ...t, count: counts[t.key].data }))} />

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <SearchField
          placeholder="Search model, SKU or serial…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          leftIcon={<Search size={16} />}
          aria-label="Search inventory" />
        <Select placeholder="All brands" options={(brandList.data || []).map((b) => ({ value: String(b.id), label: b.name }))} value={brand} onChange={setBrand} searchable allowClear closeOnSelect />
        <Select placeholder="All grades" options={gradeOptions()} value={grade} onChange={setGrade} allowClear closeOnSelect />
        <Select placeholder="All conditions" options={conditionOptions(registry.conditions)} value={condition} onChange={setCondition} allowClear closeOnSelect />
      </div>

      <QueryState query={units}>
        {() =>
          rows.length ? (
            <>
              <Table
                data={rows}
                columns={columns}
                onRowClick={(u) => setOpen(u)}
                pagination
                pageSize={25}
                showSerial={false}
                withAction={false}
                showColumnPicker={false}
                onReload={reloadAll}
                mobileMaxFields={5}
              />
              {units.totalCount > LIMIT ? <p className="text-xs opacity-60">Showing the newest {LIMIT} of {units.totalCount} units; narrow with the filters above.</p> : null}
            </>
          ) : (
            <EmptyState
              title={filtered || tab !== 'all' ? 'No units match' : 'No inventory yet'}
              action={!filtered ? <Can module="inventory" action="create"><Button onClick={() => nav('/add')}>Add your first product</Button></Can> : null}
            >
              {filtered ? 'Try clearing a filter or the search.' : 'Add devices one by one with the intake wizard, or import a CSV.'}
            </EmptyState>
          )
        }
      </QueryState>

      <UnitDrawer unit={open} onClose={() => setOpen(null)} onChanged={() => { setOpen(null); reloadAll(); }} />
    </div>
  );
}
